import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import { Form, Modal, Select, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { getInstitutions } from "../../services/institutionService";
import { getProgramLevels } from "../../services/ProgramLevel";
import {
  createAcademicProgram,
  deleteAcademicProgram,
  getAcademicPrograms,
  updateAcademicProgram,
} from "../../services/academicProgramService";
import { academicProgramSchema } from "../../validators/academicProgramValidators";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const AcademicProgram = () => {
  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [programToEdit, setProgramToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [programToDelete, setProgramToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [academicProgramLevels, setAcademicProgramLevels] = useState([]);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [selectedAcademicProgramLevels, setSelectedAcademicProgramLevels] =
    useState("Not Selected");

  const { user } = useStateContext();

  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadPrograms();
    loadInstitutions();
    loadAcademicProgramLevels();
  }, []);

  const statusOptions = [
    { id: "active", name: "Active" },
    { id: "inactive", name: "Inactive" },
  ];

  useEffect(() => {
    let filtered = programs;
    if (searchTerm.trim() !== "") {
      filtered = programs.filter((item) =>
        `${item.name} ${item?.institution?.name} ${item?.program_code} ${item?.duration} ${item.academic_program_level?.name}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    if (selectedAcademicProgramLevels !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.academic_program_level?.id?.toString() ===
          selectedAcademicProgramLevels?.toString()
      );
    }

    setFilteredPrograms(filtered);
  }, [
    searchTerm,
    programs,
    selectedInstitution,
    selectedAcademicProgramLevels,
  ]);

  const loadInstitutions = async () => {
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      toast.error("Failed to load institutions");
    }
  };

  const loadAcademicProgramLevels = async () => {
    try {
      const { data } = await getProgramLevels();
      setAcademicProgramLevels(data.data);
    } catch (error) {
      toast.error("Failed to load academic program levels");
    }
  };

  const loadPrograms = async () => {
    setLoading(true);
    try {
      const { data } = await getAcademicPrograms();

      setPrograms(data.data);
      setFilteredPrograms(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load programs");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (programToEdit) {
      onUpdateProgram(payload, resetForm);
    } else {
      onCreateProgram(payload, resetForm);
    }
  };

  const onCreateProgram = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createAcademicProgram(payload);

      setPrograms((prev) => [...prev, data.data]);

      toast.success("Academic program added successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Academic program was not added, try again!"
        );
      }
    }
  };

  const handleEdit = (category) => {
    setProgramToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdateProgram = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalPrograms = [...programs];

      const programIndex = originalPrograms.findIndex(
        (program) => program.id === programToEdit.id
      );

      if (programIndex === -1) throw new Error("Academic program not found.");

      const { data } = await updateAcademicProgram(programToEdit.id, payload);

      originalPrograms[programIndex] = data.data;

      setPrograms(originalPrograms);

      setProgramToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Academic program updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Academic program not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setProgramToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteProgram = async () => {
    const originalPrograms = [...programs];

    const newPrograms = originalPrograms.filter(
      (program) => program.id !== programToDelete.id
    );

    setPrograms(newPrograms);

    try {
      await deleteAcademicProgram(programToDelete?.id);

      toast.success("Academic program deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Academic program not deleted, try again later"
        }`
      );

      setPrograms(originalPrograms);
    } finally {
      setIsDeleteModalOpen(false);

      setProgramToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setProgramToEdit(null);
    setProgramToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),
    {
      title: "Program Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Program Code",
      dataIndex: "program_code",
      key: "program_code",
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
    },

    {
      title: "Status",
      render: (_, record) => (
        <Tag
          color={record?.status.toLowerCase() === "active" ? "green" : "red"}
        >
          {record?.status ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Program Level",
      dataIndex: ["academic_program_level", "name"],
      key: "academic_program_level",
      render: (text) => (
        <span className="text-gray-700 text-sm">{text || "N/A"}</span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Academic Program">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Academic Program">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Academic Programs" subtitle1="Academic Programs" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Academic Programs"
            count={filteredPrograms.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Academic Program"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredPrograms.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                {!isInstAdmin && (
                  <div className="mb-2 py-2 flex gap-2">
                    <Select
                      multiple={false}
                      id="institutions"
                      defaultValue={selectedInstitution}
                      className="w-full h-[36px] min-w-[250px]"
                      onChange={(value) => {
                        setSelectedInstitution(value);
                      }}
                      showSearch={true}
                      filterOption={(input, option) =>
                        option?.children
                          ?.toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      <Select.Option value={"Not Selected"}>
                        Filter by Institution
                      </Select.Option>
                      {institutions &&
                        institutions.length > 0 &&
                        institutions.map((option) => (
                          <Select.Option key={option.id} value={option.id}>
                            {option.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </div>
                )}

                <Select
                  multiple={false}
                  id="academic_program_levels"
                  defaultValue={selectedAcademicProgramLevels}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => {
                    setSelectedAcademicProgramLevels(value);
                  }}
                  showSearch={true}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  <Select.Option value={"Not Selected"}>
                    Filter by Academic Program Level
                  </Select.Option>
                  {academicProgramLevels &&
                    academicProgramLevels.length > 0 &&
                    academicProgramLevels.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                </Select>
              </div>
            }
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {programToEdit
                    ? "Edit Academic Program"
                    : "Add Academic Program"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                name: programToEdit ? programToEdit.name : "",
                program_code: programToEdit ? programToEdit.program_code : "",

                institution_id: programToEdit
                  ? programToEdit?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
                academic_program_level_id: programToEdit
                  ? programToEdit.academic_program_level?.id
                  : "",
                duration: programToEdit ? programToEdit.duration : "",
                description: programToEdit ? programToEdit.description : "",
                status: programToEdit ? programToEdit.status : "",
              }}
              enableReinitialize
              validationSchema={academicProgramSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                setFieldTouched,
                submitForm,
              }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    <TextInput
                      name="name"
                      title="Program Name"
                      placeholder="e.g. Bachelors Degree in Computer Science"
                      requiredField
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <TextInput
                        name="program_code"
                        title="Program Code"
                        placeholder="e.g. BCS001"
                        requiredField
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                      />

                      <TextInput
                        name="duration"
                        title="Duration"
                        placeholder="e.g. 3 years"
                        requiredField
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <CustomDropDownInput
                        name="academic_program_level_id"
                        title="Program Level"
                        placeholder="Select a program level"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={academicProgramLevels}
                        setFieldValue={setFieldValue}
                      />

                      <CustomDropDownInput
                        name="status"
                        title="Status"
                        placeholder="Select a status"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={statusOptions}
                        setFieldValue={setFieldValue}
                      />
                    </div>

                    {isInstAdmin ? (
                      <input
                        type="hidden"
                        name="institution_id"
                        value={
                          programToEdit
                            ? programToEdit?.institution?.id
                            : user?.institution?.id
                        }
                      />
                    ) : (
                      <CustomDropDownInput
                        name="institution_id"
                        title="Institution"
                        placeholder="Select an institution"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={institutions}
                        setFieldValue={setFieldValue}
                      />
                    )}

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteProgram}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default AcademicProgram;
